// dashboard/tren.view — panel "Tren" di Dashboard: satu indikator, digambar sepanjang waktu,
// boleh dipecah per cabang / teknisi / tipe pelanggan.
//
// Dipisah dari dashboard.view (yang sudah >1000 baris) supaya keduanya tetap bisa dibaca.
// Semua hitungan ada di `@domain/tren` — berkas ini hanya menggambar dan menyimpan pilihan.
import { esc, rp } from '@domain/format';
import {
  METRIK, bangunTren, bangunMulti, satuanSeragam, bandingPeriode, periodeTersedia, keIndeks, analisaSeri, infoBerjalan, metrikById,
  type MetrikId, type PecahId, type PeriodeId, type TiketTren, type HasilTren, type Satuan, type JenisBanding,
} from '@domain/tren';

export interface PilihanTren { metrik: MetrikId[]; pecah: PecahId; periode: PeriodeId; indeks: boolean;
  /** Mode banding: dua periode ditumpuk pada sumbu relatif. null = mati. */
  banding?: { a: string; b: string } | null }
export const PILIHAN_AWAL: PilihanTren = { metrik: ['omzet'], pecah: 'total', periode: 'bulan', indeks: false };

/** Indikator yang menyingkap modal/HPP — disembunyikan dari peran yang tak boleh melihat modal. */
const BUTUH_MODAL: MetrikId[] = ['laba', 'margin'];

/** Pilihan tersimpan bisa berasal dari versi lama (satu indikator) atau memuat yang tak boleh dilihat. */
export function bacaPilihan(mentah: unknown, bolehModal: boolean): PilihanTren {
  const p = (mentah ?? {}) as Partial<PilihanTren> & { metrik?: MetrikId | MetrikId[] };
  const daftar = Array.isArray(p.metrik) ? p.metrik : p.metrik ? [p.metrik] : PILIHAN_AWAL.metrik;
  const boleh = daftar.filter((id) => METRIK.some((m) => m.id === id) && (bolehModal || !BUTUH_MODAL.includes(id)));
  return {
    metrik: boleh.length ? boleh : ['omzet'],
    pecah: p.pecah ?? PILIHAN_AWAL.pecah,
    periode: p.periode ?? PILIHAN_AWAL.periode,
    indeks: !!p.indeks,
    banding: p.banding && p.banding.a && p.banding.b ? { a: p.banding.a, b: p.banding.b } : null,
  };
}

/** Warna seri — sengaja dibedakan bentuk terangnya supaya masih terbaca saat dicetak hitam-putih. */
const WARNA = ['#1FA9E0', '#1d9e75', '#b9770f', '#7c5cd6', '#c0392b', '#6b7a8a'];

const angkaSingkat = (v: number): string => {
  const a = Math.abs(v);
  if (a >= 1e9) return (v / 1e9).toFixed(a >= 1e10 ? 0 : 1).replace('.', ',') + ' M';
  if (a >= 1e6) return (v / 1e6).toFixed(a >= 1e7 ? 0 : 1).replace('.', ',') + ' jt';
  if (a >= 1e3) return Math.round(v / 1e3) + ' rb';
  return String(Math.round(v));
};

/** Angka pada sumbu & tooltip, mengikuti satuan metriknya. */
export function fmtNilai(v: number | null, s: Satuan, pendek = false): string {
  if (v == null) return '—';
  if (s === 'rp') return pendek ? angkaSingkat(v) : rp(Math.round(v));
  if (s === 'pct') return `${(Math.round(v * 10) / 10).toString().replace('.', ',')}%`;
  if (s === 'hari') return `${(Math.round(v * 10) / 10).toString().replace('.', ',')} hari`;
  if (s === 'skor') return (Math.round(v * 10) / 10).toString().replace('.', ',');
  return Math.round(v).toLocaleString('id-ID');
}

// ---------------------------------------------------------------------------------------------
// Grafik

const W = 900, H = 260, PAD_KIRI = 64, PAD_KANAN = 16, PAD_ATAS = 14, PAD_BAWAH = 34;

interface Skala { x: (i: number) => number; y: (v: number) => number; min: number; maks: number }

function buatSkala(hasil: HasilTren, nilai: (number | null)[][], satuan: Satuan, tinggi = H): Skala {
  const semua = nilai.flat().filter((v): v is number => v != null);
  // Uang & hitungan selalu mulai dari nol: memulai dari angka lain melebih-lebihkan naik-turunnya.
  // Skor kepuasan dikecualikan — skalanya memang 1–5, dan memaksanya mulai 0 membuat perubahan
  // 4,0 → 4,5 (yang berarti bagi pemilik) nyaris tak terlihat.
  let min = satuan === 'skor' ? 1 : Math.min(0, ...semua);
  let maks = satuan === 'skor' ? 5 : Math.max(...semua, 0);
  if (!Number.isFinite(min) || !Number.isFinite(maks) || min === maks) { min = 0; maks = maks || 1; }
  const n = Math.max(1, hasil.label.length - 1);
  const lebar = W - PAD_KIRI - PAD_KANAN, tg = tinggi - PAD_ATAS - PAD_BAWAH;
  return {
    min, maks,
    x: (i) => PAD_KIRI + (lebar * i) / n,
    y: (v) => PAD_ATAS + tg - ((v - min) / (maks - min || 1)) * tg,
  };
}

/** Garis putus saat datanya memang tidak ada — bukan disambung dengan angka karangan. */
function jalurGaris(nilai: (number | null)[], sk: Skala): string {
  let d = '', angkat = true;
  nilai.forEach((v, i) => {
    if (v == null) { angkat = true; return; }
    d += `${angkat ? 'M' : 'L'}${sk.x(i).toFixed(1)},${sk.y(v).toFixed(1)} `;
    angkat = false;
  });
  return d.trim();
}

function grafikSVG(hasil: HasilTren, nilai: (number | null)[][], satuan: Satuan, tinggi = H): string {
  const sk = buatSkala(hasil, nilai, satuan, tinggi);
  const n = hasil.label.length;

  // Sumbu Y: 4 garis bantu, dibulatkan supaya angkanya enak dibaca.
  const langkah = (sk.maks - sk.min) / 4;
  const grid = Array.from({ length: 5 }, (_, i) => {
    const v = sk.min + langkah * i, y = sk.y(v);
    return `<line x1="${PAD_KIRI}" y1="${y.toFixed(1)}" x2="${W - PAD_KANAN}" y2="${y.toFixed(1)}" stroke="var(--line)" stroke-width="1"/>
      <text x="${PAD_KIRI - 8}" y="${(y + 3.5).toFixed(1)}" text-anchor="end" font-size="10" fill="var(--muted)">${esc(fmtNilai(v, satuan, true))}</text>`;
  }).join('');

  // Sumbu X: tampilkan sebagian label saja kalau titiknya banyak, supaya tak tumpang tindih.
  const lompat = Math.max(1, Math.ceil(n / 12));
  const sumbuX = hasil.label.map((l, i) => (i % lompat === 0 || i === n - 1)
    ? `<text x="${sk.x(i).toFixed(1)}" y="${tinggi - 12}" text-anchor="middle" font-size="10" fill="var(--muted)">${esc(l)}</text>` : '').join('');

  const garis = hasil.seri.map((s, si) => {
    const w = WARNA[si % WARNA.length]!;
    const d = jalurGaris(nilai[si]!, sk);
    const titik = nilai[si]!.map((v, i) => v == null ? ''
      : `<circle cx="${sk.x(i).toFixed(1)}" cy="${sk.y(v).toFixed(1)}" r="${n > 40 ? 0 : 3}" fill="${w}"><title>${esc(s.nama)} · ${esc(hasil.label[i] ?? '')}: ${esc(fmtNilai(v, satuan))}</title></circle>`).join('');
    return `<path d="${d}" fill="none" stroke="${w}" stroke-width="2.2" stroke-linejoin="round" stroke-linecap="round"/>${titik}`;
  }).join('');

  const nol = sk.min < 0 ? `<line x1="${PAD_KIRI}" y1="${sk.y(0).toFixed(1)}" x2="${W - PAD_KANAN}" y2="${sk.y(0).toFixed(1)}" stroke="var(--muted)" stroke-width="1" stroke-dasharray="3 3"/>` : '';

  return `<svg viewBox="0 0 ${W} ${tinggi}" style="width:100%;height:auto;display:block" role="img"
    aria-label="Grafik tren ${esc(hasil.seri.map((x) => x.nama).join(', '))}">${grid}${nol}${garis}${sumbuX}</svg>`;
}

// ---------------------------------------------------------------------------------------------
// Panel

const LABEL_PERIODE: Record<PeriodeId, string> = { hari: 'Harian', minggu: 'Mingguan', bulan: 'Bulanan', kuartal: 'Kuartal', tahun: 'Tahunan' };
const LABEL_PECAH: Record<PecahId, string> = { total: 'Tanpa dipecah', cabang: 'Per cabang', teknisi: 'Per teknisi', tipe: 'Toko vs User' };

/** Kalimat tindakan — hanya untuk arah yang benar-benar terbaca, dan spesifik ke metriknya. */
function saran(id: MetrikId, arah: string, naikBaik: boolean): string {
  if (arah === 'belum') return 'Kumpulkan beberapa periode lagi sebelum menyimpulkan apa pun.';
  if (arah === 'kosong') return 'Belum ada transaksi pada rentang ini.';
  if (arah === 'datar') return 'Belum ada perubahan berarti — perubahan kecil di sini masih wajar.';
  const baik = (arah === 'naik') === naikBaik;
  const per: Partial<Record<MetrikId, [string, string]>> = {
    omzet: ['Cari tahu cabang/teknisi mana yang menarik kenaikan ini, lalu tiru caranya.', 'Periksa unit masuk dulu: sepi pelanggan atau nilai servisnya yang mengecil?'],
    laba: ['Untung membaik — pastikan bukan karena menunda belanja sparepart.', 'Bandingkan dengan margin: naiknya modal atau turunnya harga?'],
    margin: ['Margin membaik — pertahankan harga dan pilihan sparepart yang sekarang.', 'Kerja bisa jadi tetap ramai tapi untungnya menipis. Cek harga sparepart & diskon.'],
    aov: ['Nilai per servis membesar — pekerjaan makin bernilai.', 'Servis makin kecil-kecil. Tawarkan pemeriksaan menyeluruh saat unit masuk.'],
    piutang: ['Piutang menyusut — penagihan bekerja.', 'Uang makin banyak tertahan di pelanggan. Buka menu Penagihan sebelum menumpuk.'],
    masuk: ['Permintaan naik — siapkan kapasitas teknisi & stok sparepart.', 'Unit masuk menurun. Ini soal pemasaran, bukan soal bengkel.'],
    selesai: ['Penyelesaian meningkat.', 'Unit selesai menurun padahal masuk tetap? Berarti menumpuk di dalam.'],
    lama: ['Pengerjaan makin cepat.', 'Antrian makin lama. Cek unit yang tertahan di Dikerjakan & Menunggu Persetujuan.'],
    gagal: ['Kegagalan menurun.', 'Makin banyak yang tak bisa diperbaiki. Periksa alat, sparepart, dan cara diagnosa.'],
    ulang: ['Servis ulang menurun — mutu membaik.', 'Makin banyak unit kembali. Ini biaya tersembunyi: dikerjakan dua kali, dibayar sekali.'],
    csat: ['Kepuasan naik — pertahankan.', 'Kepuasan turun. Baca keterangan survei bintang rendah satu per satu.'],
  };
  const pasangan = per[id];
  return pasangan ? (baik ? pasangan[0] : pasangan[1]) : (baik ? 'Arahnya sehat.' : 'Arahnya perlu diperiksa.');
}

const BLN_PENDEK = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];

/** Nama periode untuk pilihan pembanding: "Agu 2026", "K3 2026", "2026". */
function labelPeriodeBanding(kunci: string, jenis: JenisBanding): string {
  if (jenis === 'tahun') return kunci;
  if (jenis === 'kuartal') { const [y, k] = kunci.split('-'); return `${k} ${y}`; }
  const [y, m] = kunci.split('-');
  return `${BLN_PENDEK[Number(m) - 1] ?? m} ${y}`;
}

/**
 * Ringkasan mode banding. Yang berguna di sini bukan "arah tren" melainkan SELISIH pada titik
 * terakhir yang sama-sama punya angka — membandingkan bulan berjalan dengan bulan penuh itu
 * yang paling sering membuat orang salah menyimpulkan.
 */
function bandingRingkas(hasil: HasilTren, def: { label: string; satuan: Satuan; naikBaik: boolean }): string {
  const [a, b] = hasil.seri;
  if (!a || !b) return '';
  // Titik terakhir yang KEDUA periode sama-sama punya angkanya — itu satu-satunya titik adil.
  let iSama = -1;
  a.nilai.forEach((v, i) => { if (v != null && b.nilai[i] != null) iSama = i; });
  const kartu = (s: { nama: string; nilai: (number | null)[] }, warna: string): string =>
    `<div class="panel" style="padding:9px 11px;margin:0 0 7px">
      <div style="display:flex;justify-content:space-between;gap:10px;align-items:baseline">
        <b style="font-size:12.5px;color:${warna}">${esc(s.nama)}</b>
        <span style="font-size:12.5px;font-weight:700">${esc(fmtNilai(iSama >= 0 ? s.nilai[iSama] ?? null : null, def.satuan))}</span></div>
      <div style="font-size:11px;color:var(--muted);margin-top:2px">${esc(def.label)} s/d titik ke-${iSama + 1}</div></div>`;
  let banding = '';
  if (iSama >= 0) {
    const va = a.nilai[iSama]!, vb = b.nilai[iSama]!;
    const beda = va - vb;
    const persen = vb !== 0 ? Math.round((beda / Math.abs(vb)) * 1000) / 10 : null;
    const baik = (beda > 0) === def.naikBaik;
    const warna = beda === 0 ? 'var(--muted)' : baik ? 'var(--ok)' : 'var(--danger)';
    banding = `<div class="panel" style="padding:9px 11px;margin:0 0 7px;border-color:${warna}">
      <div style="font-size:12.5px;font-weight:700;color:${warna}">
        ${beda === 0 ? 'Sama persis' : `${beda > 0 ? '▲ Lebih tinggi' : '▼ Lebih rendah'} ${esc(fmtNilai(Math.abs(beda), def.satuan))}${persen != null ? ` (${Math.abs(persen).toString().replace('.', ',')}%)` : ''}`}</div>
      <div style="font-size:11.5px;color:var(--muted);margin-top:3px">Dibandingkan pada titik yang sama, supaya periode berjalan tidak diadu dengan periode penuh.</div></div>`;
  }
  return banding + kartu(a, WARNA[0]!) + kartu(b, WARNA[1]!);
}

/** Render panel Tren ke dalam `host`. Pemanggil menyediakan data & penyimpan pilihan. */
/**
 * Rentang yang ditampilkan mengikuti satuan periodenya: cukup panjang untuk melihat bentuk tren,
 * cukup pendek supaya titiknya tidak berdempetan sampai tak terbaca.
 */
function rentangUntuk(p: PeriodeId, kini = new Date()): { dari: string; sampai: string } {
  const iso = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const sampai = iso(kini);
  const d = new Date(kini);
  if (p === 'hari') d.setDate(d.getDate() - 29);            // 30 hari
  else if (p === 'minggu') d.setDate(d.getDate() - 7 * 11); // 12 minggu
  else if (p === 'bulan') d.setMonth(d.getMonth() - 11);    // 12 bulan
  else if (p === 'kuartal') d.setMonth(d.getMonth() - 3 * 7); // 8 kuartal (2 tahun)
  else d.setFullYear(d.getFullYear() - 4);                  // 5 tahun
  return { dari: p === 'bulan' || p === 'kuartal' || p === 'tahun' ? iso(d).slice(0, 8) + '01' : iso(d), sampai };
}

export interface OpsiRender {
  /** Peran ini boleh melihat modal/HPP? Menentukan Laba Kotor & Margin ditampilkan atau tidak. */
  bolehModal: boolean;
  /** Keterangan lingkup data — supaya jelas grafik ini bicara tentang cabang/orang yang mana. */
  lingkup?: string;
}

export function renderTren(
  host: HTMLElement,
  tiket: TiketTren[],
  pilihan: PilihanTren,
  simpan: (p: PilihanTren) => void,
  opsi: OpsiRender = { bolehModal: true },
): void {
  const daftarMetrik = METRIK.filter((m) => opsi.bolehModal || !BUTUH_MODAL.includes(m.id));
  let besar = false;                                    // mode layar penuh

  const gambar = (): void => {
    const defPertama = metrikById(pilihan.metrik[0]!);
    const banyak = pilihan.metrik.length > 1;
    let modeBanding = !!pilihan.banding;
    // Jenis periode yang bisa dibandingkan: harian & mingguan tak punya "periode sebelumnya" yang
    // setara untuk ditumpuk, jadi banding memakai bulan/kuartal/tahun saja.
    const jenisBanding: JenisBanding = pilihan.periode === 'kuartal' || pilihan.periode === 'tahun' ? pilihan.periode : 'bulan';
    const tersedia = periodeTersedia(tiket, jenisBanding, defPertama.basis);
    // Pilihan pembanding menyimpan kunci periode ("2026-08"). Begitu jenisnya berubah — misalnya
    // pengguna pindah dari Bulanan ke Kuartal — kunci lama tak lagi berlaku dan grafiknya jadi
    // kosong tanpa sebab yang terlihat. Jadi pilihan yang tak dikenali disetel ulang ke dua
    // periode terbaru yang benar-benar punya data.
    const bandingSah = pilihan.banding && tersedia.includes(pilihan.banding.a) && tersedia.includes(pilihan.banding.b)
      ? pilihan.banding
      : pilihan.banding && tersedia.length
        ? { a: tersedia[0]!, b: tersedia[1] ?? tersedia[0]! }
        : null;

    // Membandingkan beberapa indikator hanya masuk akal tanpa dipecah — kalau tidak, 3 indikator ×
    // 5 cabang jadi 15 garis dan grafiknya berhenti menjelaskan apa pun.
    const pecahAktif: PecahId = banyak || modeBanding ? 'total' : pilihan.pecah;
    modeBanding = !!bandingSah;
    const hasil = bandingSah
      ? bandingPeriode(tiket, { metrik: pilihan.metrik[0]!, jenis: jenisBanding, a: bandingSah.a, b: bandingSah.b })
      : banyak
        ? bangunMulti(tiket, { metrik: pilihan.metrik, periode: pilihan.periode, ...rentangUntuk(pilihan.periode) })
        : bangunTren(tiket, { metrik: pilihan.metrik[0]!, pecah: pecahAktif, periode: pilihan.periode, ...rentangUntuk(pilihan.periode) });

    // Satuan campur (Rupiah + persen) tak boleh satu sumbu — 300.000 dan 75% tidak sebanding.
    // Dalam hal itu grafik dipaksa ke Indeks 100: yang dibandingkan bentuk pertumbuhannya.
    const satuanBersama = banyak && !modeBanding ? satuanSeragam(pilihan.metrik) : defPertama.satuan;
    const paksaIndeks = satuanBersama == null;
    const pakaiIndeks = (pilihan.indeks || paksaIndeks) && !modeBanding;
    const nilai = hasil.seri.map((s) => (pakaiIndeks ? keIndeks(s.nilai) : s.nilai));
    const satuan: Satuan = pakaiIndeks ? 'angka' : (satuanBersama ?? 'angka');

    // Kosong = tak ada satu pun angka (tak ada serinya, atau semuanya kosong). NOL BUKAN kosong:
    // "bulan itu tidak ada omzet" adalah kabar penting dan harus terlihat sebagai garis di nol.
    // Grafik kosong yang diam membuat orang mengira aplikasinya rusak.
    const adaAngka = nilai.some((baris) => baris.some((v) => v != null));

    const chipMetrik = daftarMetrik.map((m) => {
      const on = pilihan.metrik.includes(m.id);
      return `<button type="button" data-m="${m.id}" class="tren-chip${on ? ' on' : ''}" title="${esc(m.jelas)}" aria-pressed="${on}">${on ? '✓ ' : ''}${esc(m.label)}</button>`;
    }).join(' ');

    const pilihPeriode = (['hari', 'minggu', 'bulan', 'kuartal', 'tahun'] as PeriodeId[]).map((p) =>
      `<button type="button" data-p="${p}" class="tren-chip${p === pilihan.periode ? ' on' : ''}">${LABEL_PERIODE[p]}</button>`).join(' ');

    const matiPecah = banyak || modeBanding;
    const pilihPecah = `<select id="tr_pecah" class="tren-pilih"${matiPecah ? ' disabled title="Nonaktif saat membandingkan"' : ''}>${
      (['total', 'cabang', 'teknisi', 'tipe'] as PecahId[]).map((p) =>
        `<option value="${p}"${p === pilihan.pecah ? ' selected' : ''}>${LABEL_PECAH[p]}</option>`).join('')}</select>`;

    const tombolIndeks = `<button type="button" id="tr_indeks" class="tren-chip${pakaiIndeks ? ' on' : ''}"${paksaIndeks || modeBanding ? ' disabled' : ''}
      title="${paksaIndeks ? 'Wajib dipakai karena satuan indikatornya berbeda' : 'Semua garis dimulai dari 100 supaya yang besar & kecil bisa dibandingkan bentuk pertumbuhannya'}">Indeks 100</button>`;

    const tombolBanding = `<button type="button" id="tr_banding" class="tren-chip${modeBanding ? ' on' : ''}"
      title="Tumpuk dua periode pada sumbu yang sama — menjawab 'sampai tanggal ini, lebih baik dari periode lalu tidak?'">⇄ Bandingkan</button>`;

    const opsiPeriode = (dipilih: string): string => tersedia.map((k) =>
      `<option value="${esc(k)}"${k === dipilih ? ' selected' : ''}>${esc(labelPeriodeBanding(k, jenisBanding))}</option>`).join('');
    const barisBanding = modeBanding
      ? `<div class="tren-banding">
          <span style="font-size:11.5px;color:var(--muted)">Bandingkan</span>
          <select id="tr_ba" class="tren-pilih">${opsiPeriode(bandingSah!.a)}</select>
          <span style="font-size:12px;color:var(--muted)">vs</span>
          <select id="tr_bb" class="tren-pilih">${opsiPeriode(bandingSah!.b)}</select>
          ${tersedia.length < 2 ? '<span style="font-size:11.5px;color:var(--warn)">Baru ada satu periode berisi data.</span>' : ''}
        </div>`
      : '';

    const tombolBesar = `<button type="button" id="tr_besar" class="tren-chip" title="${besar ? 'Kembalikan ukuran' : 'Perbesar ke layar penuh'}">${besar ? '✕ Tutup' : '⛶ Perbesar'}</button>`;

    const legenda = hasil.seri.map((s, i) =>
      `<span style="display:inline-flex;align-items:center;gap:6px">
        <span style="width:11px;height:11px;border-radius:50%;border:2.5px solid ${WARNA[i % WARNA.length]};display:inline-block"></span>${esc(s.nama)}</span>`).join('');

    // Mode banding: yang berguna bukan "arah tren", tapi SELISIHNYA sampai titik yang sama.
    const analisa = modeBanding ? bandingRingkas(hasil, defPertama) : hasil.seri.slice(0, banyak ? 6 : 3).map((s, i) => {
      const idMetrik = banyak ? pilihan.metrik[i]! : pilihan.metrik[0]!;
      const d = metrikById(idMetrik);
      const a = analisaSeri(s.nilai, d.satuan);
      const warna = a.arah === 'belum' || a.arah === 'kosong' || a.arah === 'datar' ? 'var(--muted)'
        : (a.arah === 'naik') === d.naikBaik ? 'var(--ok)' : 'var(--danger)';
      return `<div class="panel" style="padding:9px 11px;margin:0 0 7px">
        <div style="display:flex;justify-content:space-between;gap:10px;align-items:baseline">
          <b style="font-size:12.5px;color:${WARNA[i % WARNA.length]}">${esc(s.nama)}</b>
          <span style="font-size:11.5px;color:${warna};font-weight:600">${esc(a.teks)}</span></div>
        <div style="font-size:11.5px;color:var(--muted);margin-top:3px">${esc(saran(idMetrik, a.arah, d.naikBaik))}</div></div>`;
    }).join('');

    // Catatan periode berjalan: titik terakhir sering terlihat anjlok padahal belum selesai.
    const kunciAkhir = hasil.kunci[hasil.kunci.length - 1] ?? '';
    const nilaiAkhir = hasil.seri[0]?.nilai[hasil.kunci.length - 1] ?? null;
    const brj = adaAngka ? infoBerjalan(pilihan.periode, kunciAkhir, new Date(), nilaiAkhir) : null;
    const catatanBerjalan = brj
      ? `<div class="scope-note" style="margin-top:8px"><b>${esc(hasil.label[hasil.label.length - 1] ?? '')} masih berjalan</b> — baru ${brj.lewat} dari ${brj.total} hari, jadi titik terakhir wajar terlihat rendah dibanding periode penuh.${
          brj.proyeksi != null && !pakaiIndeks ? ` Bila lajunya bertahan, <b>${esc(hasil.seri[0]?.nama ?? '')}</b> berakhir sekitar <b>${esc(fmtNilai(brj.proyeksi, defPertama.satuan))}</b>.` : ''}</div>`
      : '';

    const basisNote = defPertama.basis === 'ambil'
      ? 'Dihitung per tanggal unit <b>diambil</b> (saat uangnya diakui), bukan tanggal masuk.'
      : 'Dihitung per tanggal unit <b>diterima</b> — ini beban kerja, terlepas kapan uangnya masuk.';

    const kosongHTML = `<div class="empty" style="padding:26px 12px;text-align:center;line-height:1.7">
      Belum ada angka untuk pilihan ini.<br>
      <span style="font-size:12px;color:var(--muted)">${pilihan.pecah === 'total'
        ? 'Coba periode yang lebih panjang, atau indikator lain.'
        : `Tak ada data yang bisa dipecah ${esc(LABEL_PECAH[pilihan.pecah].toLowerCase())} pada rentang ini.`}</span></div>`;

    host.innerHTML = `
      <div class="panel tren-wrap${besar ? ' tren-besar' : ''}" style="padding:0">
        <div class="tren-kepala">
          <b style="color:var(--navy);margin-right:auto">📈 Tren</b>
          ${pilihPeriode} ${pilihPecah} ${tombolIndeks} ${tombolBanding} ${tombolBesar}
        </div>
        ${barisBanding}
        <div style="padding:10px 12px 0;display:flex;flex-wrap:wrap;gap:6px">${chipMetrik}</div>
        <div style="padding:6px 12px 0;font-size:11.5px;color:var(--muted)">Klik beberapa indikator untuk membandingkannya dalam satu grafik.</div>
        <div style="display:flex;flex-wrap:wrap;gap:12px;padding:8px 12px 12px">
          <div style="flex:1 1 480px;min-width:280px">
            ${adaAngka ? grafikSVG(hasil, nilai, satuan) : kosongHTML}
            <div class="tren-legenda">${adaAngka ? legenda : ''}</div>
          </div>
          ${adaAngka ? `<div style="flex:1 1 230px;min-width:210px;max-width:340px">${analisa}</div>` : ''}
        </div>
        <div style="padding:0 12px 12px">
          ${modeBanding
            ? `<div class="scope-note">Dua periode ditumpuk pada sumbu yang sama dan dihitung <b>menumpuk</b> (kumulatif), supaya "sampai tanggal ini" bisa dibandingkan langsung. Periode berjalan berhenti di hari ini — sisanya sengaja tak digambar.</div>`
            : banyak
              ? `<div class="scope-note">Sedang membandingkan <b>${pilihan.metrik.length} indikator</b>, jadi pemecahan per cabang/teknisi dimatikan sementara. Klik indikator untuk melepasnya.</div>`
              : `<div class="scope-note">${esc(defPertama.label)}: ${defPertama.jelas} ${basisNote}</div>`}
          ${paksaIndeks ? `<div class="scope-note" style="margin-top:6px">Satuan indikatornya berbeda, jadi grafik memakai <b>Indeks 100</b> — yang dibandingkan <b>bentuk naik-turunnya</b>, bukan besar angkanya. Angka sebenarnya muncul saat kursor menyentuh titik.</div>` : ''}
          ${catatanBerjalan}
        </div>
      </div>`;

    const ubah = (baru: Partial<PilihanTren>): void => { pilihan = { ...pilihan, ...baru }; simpan(pilihan); gambar(); };

    host.querySelectorAll<HTMLElement>('button[data-m]').forEach((b) => b.addEventListener('click', () => {
      const id = b.dataset['m'] as MetrikId;
      const ada = pilihan.metrik.includes(id);
      // Selalu sisakan satu indikator — grafik tanpa indikator tak punya arti.
      const baru = ada ? pilihan.metrik.filter((x) => x !== id) : [...pilihan.metrik, id];
      ubah({ metrik: baru.length ? baru : [id] });
    }));
    host.querySelectorAll<HTMLElement>('button[data-p]').forEach((b) => b.addEventListener('click', () =>
      ubah({ periode: b.dataset['p'] as PeriodeId })));
    host.querySelector('#tr_pecah')?.addEventListener('change', (e) =>
      ubah({ pecah: (e.target as HTMLSelectElement).value as PecahId }));
    host.querySelector('#tr_indeks')?.addEventListener('click', () => ubah({ indeks: !pilihan.indeks }));
    host.querySelector('#tr_banding')?.addEventListener('click', () => ubah({
      // Bawaannya: periode terbaru vs sebelumnya — perbandingan yang paling sering ditanyakan.
      banding: modeBanding ? null : (tersedia.length >= 2 ? { a: tersedia[0]!, b: tersedia[1]! } : tersedia.length === 1 ? { a: tersedia[0]!, b: tersedia[0]! } : null),
    }));
    host.querySelector('#tr_ba')?.addEventListener('change', (e) =>
      ubah({ banding: { a: (e.target as HTMLSelectElement).value, b: bandingSah!.b } }));
    host.querySelector('#tr_bb')?.addEventListener('change', (e) =>
      ubah({ banding: { a: bandingSah!.a, b: (e.target as HTMLSelectElement).value } }));
    host.querySelector('#tr_besar')?.addEventListener('click', () => { besar = !besar; gambar(); });
  };
  gambar();
}
